import { useState, useEffect, useRef, useCallback } from 'react';

export default function Preloader({ onComplete }: { onComplete: () => void }) {
  const [phase, setPhase] = useState<'init' | 'logoAppear' | 'lineGrow' | 'textAppear' | 'dissolve' | 'maskUp'>('init');
  const [visible, setVisible] = useState(true);
  const maskRef = useRef<HTMLDivElement>(null);

  const finish = useCallback(() => {
    setVisible(false);
    onComplete();
  }, [onComplete]);

  useEffect(() => {
    // Precise cinematic timeline (total ~2.8s)
    const t0 = setTimeout(() => setPhase('logoAppear'), 100);
    const t1 = setTimeout(() => setPhase('lineGrow'), 550);
    const t2 = setTimeout(() => setPhase('textAppear'), 1200);
    const t3 = setTimeout(() => setPhase('dissolve'), 1900);
    // Mask reveal — unroll upward
    const t4 = setTimeout(() => setPhase('maskUp'), 2150);
    const t5 = setTimeout(() => finish(), 2900);

    return () => {
      clearTimeout(t0); clearTimeout(t1); clearTimeout(t2);
      clearTimeout(t3); clearTimeout(t4); clearTimeout(t5);
    };
  }, [finish]);

  if (!visible) return null;

  const isDissolving = phase === 'dissolve' || phase === 'maskUp';

  return (
    <>
      {/* Mask overlay — covers the whole viewport, reveals upward */}
      <div
        ref={maskRef}
        className="fixed inset-0 z-[10000] bg-white flex flex-col items-center justify-center"
        style={{
          transition: 'clip-path 0.8s cubic-bezier(0.76, 0, 0.24, 1)',
          clipPath: phase === 'maskUp' ? 'inset(0% 0% 100% 0%)' : 'inset(0% 0% 0% 0%)',
          opacity: visible ? 1 : 0,
        }}
      >
        <div
          className="flex flex-col items-center gap-8 transition-opacity duration-600"
          style={{
            opacity: isDissolving ? 0 : 1,
            transitionDuration: '600ms',
            transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {/* Logo area — appears letter by letter feel */}
          <div className="relative flex flex-col items-center">
            <h1
              className="font-heading text-2xl md:text-3xl lg:text-4xl text-graphite font-light tracking-[0.25em] transition-all duration-1000"
              style={{
                opacity: phase === 'init' ? 0 : 1,
                transform: phase === 'init' ? 'translateY(12px)' : 'translateY(0)',
                transitionDuration: '1000ms',
                transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              DRA. NATHÁLIA POSSO
            </h1>
            <p
              className="font-body text-[9px] md:text-[10px] text-secondary-500 tracking-[0.45em] mt-3 transition-all duration-800"
              style={{
                opacity: phase === 'init' || phase === 'logoAppear' ? 0 : 0.55,
                transitionDuration: '800ms',
                transitionTimingFunction: 'ease-out',
              }}
            >
              GINECOLOGIA
            </p>
          </div>

          {/* Fine organic line — grows from center */}
          <div className="relative w-56 h-[1px] overflow-hidden">
            <div
              className="absolute inset-0"
              style={{
                background: 'linear-gradient(90deg, transparent 0%, #C4A675 20%, #C4A675 80%, transparent 100%)',
                width: phase === 'lineGrow' || phase === 'textAppear' || phase === 'dissolve' ? '100%' : '0%',
                transition: 'width 900ms cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          </div>

          {/* Tagline */}
          <p
            className="font-heading text-sm md:text-base lg:text-lg text-graphite/65 italic font-light text-center max-w-[280px] md:max-w-sm transition-all duration-700"
            style={{
              opacity: phase === 'textAppear' || phase === 'dissolve' ? 1 : 0,
              transform: phase === 'textAppear' || phase === 'dissolve' ? 'translateY(0)' : 'translateY(10px)',
              transitionDuration: '700ms',
              transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            Cuidar da sua saúde é também acolher a sua história.
          </p>
        </div>
      </div>
    </>
  );
}